import { z } from "zod";
import { activityFeedResponseSchema, activityItemSchema, activityKindSchema } from "./engagement.js";
import { adminListFilterShape, refineAdminDayRange } from "./admin-list-filters.js";

/**
 * Shared DTO **Sổ quỹ admin** — `GET /v1/admin/funds/:id/activity` (US-AE4.F7-0909-S01 · @UC9948). CHỈ ĐỌC.
 *
 * 🔴 Sổ quỹ của admin = tab "Hoạt động" của app (QĐ người dùng 29/09/2026 — "giống hệt"). Vì vậy:
 *   - query mirror `ActivityQueryDto` của app: `kind` ∈ income|expense, **vắng = Tất cả** (KHÔNG có `all`);
 *   - response **LÀ** `activityFeedResponseSchema` của app — cùng item, cùng 8 `eventType`. Không định nghĩa
 *     item thứ hai: hai bản khai rồi lệch nhau chính là mẫu drift whitelist repo đã trả giá (CLAUDE.md §3.8).
 *     US-AE4.F7-1007: item admin = item app `.extend` thêm MỘT cờ `correctable` — vẫn một bản khai, không chép.
 *
 * Redaction do server quyết ở CÙNG khối của `ActivityFeedService` nhưng theo vai admin: tiền theo
 * `canViewMoney` (vắng `amountMinor`), tên người ở dòng cá nhân theo `canViewPii`, `description` luôn null.
 */

/** Trần page-size — bằng trần của feed app (`normalizeLimit` max 100). */
export const ADMIN_FUND_ACTIVITY_MAX_LIMIT = 100;

/** Độ dài tối đa của cursor — bằng `ActivityQueryDto` của app. */
export const ADMIN_FUND_ACTIVITY_MAX_CURSOR_LENGTH = 512;

export const adminFundActivityQuerySchema = z
  .object({
    cursor: z.string().max(ADMIN_FUND_ACTIVITY_MAX_CURSOR_LENGTH).optional(),
    limit: z.coerce.number().int().min(1).max(ADMIN_FUND_ACTIVITY_MAX_LIMIT).optional(),
    kind: activityKindSchema.optional(),
    // US-AE4.F7-1006 (bổ sung): ô tìm + khoảng ngày — xem `admin-list-filters.ts`.
    ...adminListFilterShape,
  })
  .strict()
  .superRefine(refineAdminDayRange);

export type AdminFundActivityQuery = z.infer<typeof adminFundActivityQuerySchema>;

/** Một nguồn với feed của app — xem docblock đầu file. */
/**
 * 🔴 US-AE4.F7-1007 (@UC9951 · review v3 MEDIUM-1) — item Sổ quỹ admin = item feed của app (KẾ THỪA nguyên, `.extend`)
 * + đúng MỘT cờ `correctable`: lần ghi này có sửa số tiền được ở "Chi tiết hoạt động" không. Máy chủ tính bằng CHÍNH
 * luật của route sửa (`incomeCorrectability`) — UI không tự đoán từ `eventType`, vì "đã nộp quỹ" gồm cả nộp hộ (không
 * sửa ở đây) và feed không phát txType. Chỉ admin có cờ; feed của app không đổi.
 */
// QĐ người dùng 12/10/2026 — `viewerActions` (Duyệt/Từ chối/Huỷ yêu cầu chi) là việc của người TRONG nhóm; Sổ quỹ admin thấy ≠ làm
// được ⇒ bỏ hẳn khỏi hợp đồng admin (0 đổi cho màn admin — QĐ "mọi việc admin để sau").
export const adminFundActivityItemSchema = activityItemSchema.omit({ viewerActions: true }).extend({ correctable: z.boolean() });
export type AdminFundActivityItem = z.infer<typeof adminFundActivityItemSchema>;

export const adminFundActivityResponseSchema = activityFeedResponseSchema.extend({
  data: z.array(adminFundActivityItemSchema),
});

export type AdminFundActivityResponse = z.infer<typeof adminFundActivityResponseSchema>;
